/** GPU Twirl — fragment-shader port of `lib/filters/twirl.ts`.
 *
 *  Inverse-map angular rotation with linear-falloff over normalized
 *  radius. For each output pixel: compute its distance-from-center
 *  `r` normalized by half-diagonal, derive the local rotation
 *  `angle * (1 - r)` (clamped to 0 at r >= 1), rotate the output
 *  position BACK to find the source position, and sample. The
 *  "rotate back" inverse-map approach avoids the holes a
 *  forward-map produces.
 *
 *  Hardware bilinear filtering replaces the JS `bilinearSample`
 *  helper — same 2×2 weighted average, just done in the texture
 *  unit. The runtime is asked for `filter: 'linear'`.
 *
 *  ── Y-axis convention ─────────────────────────────────────────
 *  The CPU path iterates `y = 0..h-1` top-down. The runtime
 *  uploads textures with `UNPACK_FLIP_Y_WEBGL=true`, so direct
 *  `texture(u_source, v_uv)` sampling preserves DOM orientation —
 *  but the natural `v_uv * u_dims` mapping puts `px.y = 0` at the
 *  DOM BOTTOM, which is the opposite of CPU's top-down `y`. For
 *  CHIRAL operations (rotation, directional kernels) this matters:
 *  unflipped, the GPU rotates the opposite direction from the CPU,
 *  visually appearing as a Y-mirrored result.
 *
 *  Fix: compute `px` and `srcUv` with explicit `1.0 - v` flips so
 *  the shader operates in top-down (CPU-aligned) coordinates. The
 *  two flips compose to identity for symmetric kinds (Pinch, Lens
 *  Correction, etc.) — but they're still applied there for
 *  consistency and to prevent the bug from re-emerging if those
 *  kinds gain directional math later. */

import { applyIdentityCopy } from '../identity-copy';
import type { TwirlValue } from '../../filters/twirl';
import { applyShader } from '../runtime';

const TWIRL_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform vec2 u_center;
uniform float u_halfDiag;
uniform float u_maxAngle;   // angle in radians, signed
out vec4 outColor;

void main() {
  // px in CPU-aligned top-down coords (px.y = 0 at DOM top). See
  // header for the Y-flip rationale.
  vec2 px = vec2(v_uv.x * u_dims.x, (1.0 - v_uv.y) * u_dims.y) - 0.5;
  vec2 d = px - u_center;
  float r = length(d);
  float t = max(0.0, 1.0 - r / u_halfDiag);
  float localAngle = u_maxAngle * t;
  float c = cos(localAngle);
  float s = sin(localAngle);
  // Inverse-map: rotate output position BACK by localAngle to find
  // the source coord. Matches the JS:
  //   sx = cx + dx * c - dy * s
  //   sy = cy + dx * s + dy * c
  vec2 srcPx = u_center + vec2(d.x * c - d.y * s, d.x * s + d.y * c);
  // srcPx is in top-down coords; flip Y when forming srcUv so the
  // UNPACK_FLIP_Y texture is sampled at the matching DOM row.
  vec2 srcUv = vec2(
    (srcPx.x + 0.5) / u_dims.x,
    1.0 - (srcPx.y + 0.5) / u_dims.y
  );
  outColor = texture(u_source, srcUv);
}
`;

export function applyTwirlOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: TwirlValue,
): boolean {
  const angleDeg = Math.max(-720, Math.min(720, value.angle));
  if (angleDeg === 0) return applyIdentityCopy(source, target);
  const maxAngle = (angleDeg * Math.PI) / 180;
  const w = source.width;
  const h = source.height;
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  return applyShader({
    source,
    target,
    fragShaderSrc: TWIRL_FRAG,
    filter: 'linear',
    uniforms: {
      u_dims: { type: 'vec2', value: [w, h] },
      u_center: { type: 'vec2', value: [cx, cy] },
      u_halfDiag: { type: 'float', value: halfDiag },
      u_maxAngle: { type: 'float', value: maxAngle },
    },
  });
}
